import { useState } from 'react';
import { budget, lastDayOfMonth, maxPlanningPeriod, type Category } from '../lib/budget';
import { parseAmountToCents } from '../lib/money';

/**
 * Defaults the date to today when adding to the current month, and to the first
 * of the month otherwise — entering a past month's expense should land in that
 * month rather than silently jumping to today.
 */
function defaultDate(year: number, month: number): string {
  const now = new Date();
  if (now.getFullYear() === year && now.getMonth() + 1 === month) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${year}-${pad(month)}-${pad(now.getDate())}`;
  }
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

export function AddExpense({
  year,
  month,
  categories,
  onClose,
  onSaved,
}: {
  year: number;
  month: number;
  categories: Category[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [categoryId, setCategoryId] = useState<number | null>(categories[0]?.id ?? null);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(defaultDate(year, month));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const horizon = maxPlanningPeriod();
  const maxDate = lastDayOfMonth(horizon.year, horizon.month);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (categoryId === null) return;

    const cents = parseAmountToCents(amount);
    if (cents === null || cents === 0) {
      setError('Enter an amount like 12.34');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await budget.addTransaction(year, month, {
        categoryId,
        amountCents: cents,
        date,
        note: note.trim() || null,
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
      setBusy(false);
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <form className="sheet" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>Add expense</h2>

        <label>
          Amount
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            placeholder="12.34"
            autoFocus
            required
          />
        </label>

        <label>
          Category
          <select
            value={categoryId ?? ''}
            onChange={(e) => setCategoryId(Number(e.target.value))}
            required
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          Date
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={maxDate}
            required
          />
        </label>

        <label>
          Note (optional)
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            placeholder="Costco run"
          />
        </label>

        {error && <p className="error">{error}</p>}

        <div className="row">
          <button type="button" className="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={busy || !amount}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
